import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useDataProvider,
  useGetIdentity,
  useGetMany,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";

import {
  CERRADO_PERDIDO_SUGGESTION_REASON,
  canReassignProtectedLeads,
  isProtectedLeadType,
  type LeadCarouselNotice,
} from "../carouselTypes";
import { ReassignActionButton } from "../leadAssignment/ReassignActionButton";
import type { Deal } from "../types";
import type { CrmDataProvider } from "../providers/types";
import {
  GUARDIAS_KEY,
  HELP_CLASS,
  QueryBoundary,
  SectionCard,
  formatInstant,
  type GuardiasSectionProps,
} from "./guardiasShared";

/** Stored stage value of "Cerrado perdido" (a special state; no loss-reason column exists). */
const CERRADO_PERDIDO_STAGE = "cerrado_perdido";

type NoticesProps = GuardiasSectionProps & {
  ownId: Identifier | undefined;
};

/**
 * "Avisos": the carousel follow-up notices of the project, newest first. Editors (Team Leader,
 * direccion, bpo lead_manager) see every notice of the project; an asesora only her own
 * (`sales_id` = her id; RLS already limits the rows, the filter keeps the screen honest).
 * Notices carry no stored "resolved" flag and the client does not derive one, so they are listed
 * as they were written; the lead link leads to the contact log.
 */
export const GuardiasNotices = ({
  projectId,
  canEdit,
  ownId,
}: NoticesProps) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notices = useQuery({
    queryKey: [GUARDIAS_KEY, "notices", projectId],
    queryFn: () => dataProvider.getLeadCarouselNotices(projectId),
  });

  return (
    <SectionCard
      title={translate("resources.guardias.notices_title")}
      help={translate("resources.guardias.notices_help")}
    >
      <QueryBoundary query={notices}>
        {(rows) => {
          const visible = canEdit
            ? rows
            : rows.filter((row) => String(row.sales_id) === String(ownId));
          return <NoticesList rows={visible} canEdit={canEdit} />;
        }}
      </QueryBoundary>
    </SectionCard>
  );
};

/** Notice kinds that offer the Team Leader a "Reasignar" action. */
const REASSIGNABLE_KINDS: readonly LeadCarouselNotice["kind"][] = [
  "alerta_cadencia_2d",
  "reasignacion_tope",
];

/** Moves the lead to Cerrado perdido (the existing stage change) and records the reason. */
const useAcceptSuggestion = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const { identity } = useGetIdentity();

  return async (deal: Deal) => {
    try {
      await dataProvider.update<Deal>("deals", {
        id: deal.id,
        data: { stage: CERRADO_PERDIDO_STAGE },
        previousData: deal,
      });
      if (identity?.id != null) {
        try {
          await dataProvider.create("deal_notes", {
            data: {
              deal_id: deal.id,
              text: `${translate("resources.guardias.loss_reason_prefix")}: ${CERRADO_PERDIDO_SUGGESTION_REASON}`,
              date: new Date().toISOString(),
              sales_id: identity.id,
            },
          });
        } catch {
          // The stage already changed: say so, instead of a generic "could not move the lead".
          await queryClient.invalidateQueries({ queryKey: [GUARDIAS_KEY] });
          notify("resources.guardias.suggestion_note_error", {
            type: "error",
          });
          return;
        }
      }
      await queryClient.invalidateQueries({ queryKey: [GUARDIAS_KEY] });
      notify("resources.guardias.suggestion_accepted", { type: "success" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      notify(message || "resources.guardias.suggestion_error", {
        type: "error",
      });
    }
  };
};

const NoticeActions = ({
  row,
  deal,
  canEdit,
  onAccept,
}: {
  row: LeadCarouselNotice;
  deal: Deal | undefined;
  canEdit: boolean;
  onAccept: (deal: Deal) => Promise<void>;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { identity } = useGetIdentity();
  const isProtected =
    row.is_protected_lead ?? isProtectedLeadType(deal?.lead_type);

  if (row.kind === "sugerencia_cerrado_perdido") {
    // RLS on deals / deal_notes refuses anybody but the owner, direccion and lead_manager, and a
    // notice has no resolved state: an already closed lead would get a duplicate note per click.
    const canAccept =
      String(identity?.id) === String(deal?.sales_id) ||
      identity?.role === "direccion" ||
      identity?.role === "lead_manager";
    if (!deal || !canAccept || deal.stage === CERRADO_PERDIDO_STAGE) {
      return null;
    }
    return (
      <Button
        type="button"
        variant="outline"
        className="h-11 sm:h-9"
        onClick={() => void onAccept(deal)}
      >
        {translate("resources.guardias.accept_suggestion")}
      </Button>
    );
  }
  // The Team Leader (and direccion / lead_manager in scope) reassign from the notice; a protected
  // lead only for the authorized group.
  if (
    !canEdit ||
    !REASSIGNABLE_KINDS.includes(row.kind) ||
    (isProtected && !canReassignProtectedLeads(identity))
  ) {
    return null;
  }
  return (
    <ReassignActionButton
      label={translate("crm.reassign.notice_button")}
      invalidateKey={GUARDIAS_KEY}
      reassign={({ newSalesId, reason }) =>
        dataProvider.reassignLeadFromNotice({
          noticeId: row.id,
          newSalesId,
          reason,
        })
      }
    />
  );
};

const NoticesList = ({
  rows,
  canEdit,
}: {
  rows: LeadCarouselNotice[];
  canEdit: boolean;
}) => {
  const translate = useTranslate();
  const onAccept = useAcceptSuggestion();
  const dealIds = [...new Set(rows.map((row) => row.deal_id))];
  const deals = useGetMany<Deal>(
    "deals",
    { ids: dealIds },
    { enabled: dealIds.length > 0 },
  );

  if (rows.length === 0) {
    return (
      <p className={HELP_CLASS}>
        {translate("resources.guardias.notices_empty")}
      </p>
    );
  }
  const dealName = (id: Identifier) =>
    deals.data?.find((deal) => String(deal?.id) === String(id))?.name ??
    translate("resources.guardias.notice_lead_fallback", { id });

  const dealOf = (id: Identifier) =>
    deals.data?.find((deal) => String(deal?.id) === String(id));

  return (
    <ul className="divide-y">
      {rows.map((row) => {
        const deal = dealOf(row.deal_id);
        const isProtected =
          row.is_protected_lead ?? isProtectedLeadType(deal?.lead_type);
        // A protected lead's 10-minute notice never promises a reassignment.
        const kindKey =
          isProtected && row.kind === "aviso_10_min"
            ? `resources.guardias.notice_kind_protected.${row.kind}`
            : `resources.guardias.notice_kind.${row.kind}`;
        return (
          <li key={row.id} className="space-y-1 py-2">
            <Link
              to={`/deals/${row.deal_id}/show`}
              className="font-medium underline-offset-2 hover:underline"
            >
              {dealName(row.deal_id)}
            </Link>
            <p className="text-[15px]">{translate(kindKey)}</p>
            {row.kind === "alerta_cadencia_2d" && row.touch_gap_since ? (
              <p className={HELP_CLASS}>
                {translate("resources.guardias.notice_gap_since", {
                  date: formatInstant(row.touch_gap_since),
                })}
              </p>
            ) : null}
            <p className={HELP_CLASS}>{formatInstant(row.created_at)}</p>
            <NoticeActions
              row={row}
              deal={deal}
              canEdit={canEdit}
              onAccept={onAccept}
            />
          </li>
        );
      })}
    </ul>
  );
};
